// Фактура над стокови: редовете на обобщените стокови в листа на фактурата —
// под собствените ѝ редове, по група на стокова („Стокова №… · дата · вътр. №…
// · вече заприходена" и сумата ѝ). Само за четене: стоката им вече е
// заприходена и фактурата само ги свързва (избор — „Промени избора" в банера
// горе). Не участват в стрелковата навигация — нямат полета. На десктоп са
// редове на СЪЩАТА решетка (`LINE_GRID_CLASS`), под десктоп — карти.
import { Fragment } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery, DeliveryLine, Product } from "../../../../api/inventory";
import { formatMoney } from "../../components/numbers";
import { deliveryGrossOf } from "./deliveryMeta";
import { LINE_GRID_CLASS, formatQtyInput, formatUnitPrice } from "./deliveryWorksheetShared";

type Props = {
  notes: Delivery[];
  productById: Map<number, Product>;
  decimalsOf: (productId: number) => number;
};

// „Стокова №12 · 29.09.2026 · вътр. №58" / „Стокова б/н · …"
const useNoteLabel = () => {
  const { t } = useTranslation();
  return (note: Delivery) =>
    t("inventoryPage.notesRowsBand", {
      number: note.invoiceNumber ? `№${note.invoiceNumber}` : t("inventoryPage.noDocNumber"),
      date: dayjs(note.date).format("DD.MM.YYYY"),
      id: note.id,
    });
};

// Количеството така, както е въведено: брой разфасовки или базови единици
const lineQty = (line: DeliveryLine, decimals: number) =>
  line.packagingsCount != null
    ? formatQtyInput(line.packagingsCount, Number.isInteger(line.packagingsCount) ? 0 : 3)
    : formatQtyInput(line.quantity, decimals);

const vatOf = (line: DeliveryLine, product: Product | undefined) =>
  line.sumWithVat !== line.sumNoVat && product?.vatRate != null ? `${product.vatRate}%` : "—";

export const DeliveryLinkedNoteRows = ({ notes, productById, decimalsOf }: Props) => {
  const { t } = useTranslation();
  const noteLabel = useNoteLabel();
  const cell = "flex min-w-0 items-center border-r border-shade-2 px-[11px] py-2";
  return (
    <>
      {notes.map((note) => (
        <Fragment key={note.id}>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-info/25 bg-info-tint px-[14px] py-[7px] text-[12.5px]">
            <span className="font-semibold text-info-deep">↓ {noteLabel(note)}</span>
            <span className="text-body">{t("inventoryPage.notesRowsHint")}</span>
            <span className="flex-1" />
            <span className="font-sheet font-semibold text-ink">{formatMoney(deliveryGrossOf(note))}</span>
          </div>
          {note.lines.map((line) => {
            const product = productById.get(line.productId);
            return (
              <div
                key={line.id}
                className={`${LINE_GRID_CLASS} items-stretch border-b border-shade-2 bg-fill/50 text-[13px] text-body`}
              >
                <div className="flex items-center justify-center border-r border-shade-2 bg-fill text-[11px] text-info-deep">
                  ↓
                </div>
                <div className={cell}>
                  <span className="truncate font-medium text-ink-2">{line.productName}</span>
                </div>
                <div className={cell}>
                  <span className="truncate">{line.packagingName || product?.unit || "—"}</span>
                </div>
                <div className={`${cell} justify-end font-sheet`}>{lineQty(line, decimalsOf(line.productId))}</div>
                <div className={`${cell} justify-end font-sheet`}>{formatUnitPrice(line.unitPrice)}</div>
                <div className={`${cell} justify-end text-[12.5px]`}>{vatOf(line, product)}</div>
                <div className={`${cell} justify-end font-sheet`}>{formatMoney(line.sumNoVat)}</div>
                <div className={`${cell} justify-end font-sheet font-semibold text-ink-2`}>
                  {formatMoney(line.sumWithVat)}
                </div>
                <div className={`${cell} justify-center font-sheet text-[12.5px]`}>{line.batchNumber || "—"}</div>
                <div className={cell}>{line.expiryDate ? dayjs(line.expiryDate).format("DD.MM.YYYY") : "—"}</div>
                <div className={cell}>
                  <span className="truncate">{line.warehouseName || "—"}</span>
                </div>
                <div />
              </div>
            );
          })}
        </Fragment>
      ))}
    </>
  );
};

// Под десктоп ширина: групата като заглавие и по една карта на ред
export const DeliveryLinkedNoteCards = ({ notes, productById, decimalsOf }: Props) => {
  const { t } = useTranslation();
  const noteLabel = useNoteLabel();
  return (
    <div>
      {notes.map((note) => (
        <Fragment key={note.id}>
          <div className="flex flex-col gap-0.5 border-b border-info/25 bg-info-tint px-3.5 py-2 text-[12.5px]">
            <span className="flex justify-between gap-2.5 font-semibold text-info-deep">
              <span>↓ {noteLabel(note)}</span>
              <span className="font-sheet text-ink">{formatMoney(deliveryGrossOf(note))}</span>
            </span>
            <span className="text-body">{t("inventoryPage.notesRowsHint")}</span>
          </div>
          {note.lines.map((line) => {
            const product = productById.get(line.productId);
            return (
              <div
                key={line.id}
                className="flex flex-col gap-1 border-b border-shade-2 bg-fill/50 px-3.5 py-2.5 text-[12.5px] text-body"
              >
                <span className="truncate text-[13.5px] font-medium text-ink-2">{line.productName}</span>
                <span className="flex flex-wrap justify-between gap-x-2.5">
                  <span className="font-sheet">
                    {lineQty(line, decimalsOf(line.productId))} {line.packagingName || product?.unit || ""} ×{" "}
                    {formatUnitPrice(line.unitPrice)}
                  </span>
                  <span className="font-sheet font-semibold text-ink-2">{formatMoney(line.sumWithVat)}</span>
                </span>
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
};
